import { Dialog } from '@mui/material';
import React from 'react';
import { Navigate, Outlet, Route, Routes, useNavigate, useParams } from 'react-router-dom';

import { Authorized } from '@/components/atoms/Authorized';
import { IntegratorFeatureFlagged } from '@/components/atoms/IntegratorFeatureFlag';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { DrawerContainer } from '@/components/layout/DrawerContainer';
import { FullScreenLoader } from '@/components/molecules/FullScreenLoader';
import { AccountDetailsContent } from '@/components/organisms/AccountDetailsContent';
import { AccountPaymentMethods } from '@/components/organisms/AccountPaymentMethods';
import { AddContactFlowModal } from '@/components/organisms/AddContactFlowModal';
import { CreateGroupModal } from '@/components/organisms/CreateGroupModal';
import { EditUserModal } from '@/components/organisms/EditUserModal';
import { ExistingContactFlowModal } from '@/components/organisms/ExistingContactFlowModal';
import { InviteUserModal } from '@/components/organisms/InviteUserModal';
import { ManageRolesModal } from '@/components/organisms/ManageRolesModal';
import { ModalFlow } from '@/components/organisms/ModalFlow';
import { NewOrExistingContactFlowModal } from '@/components/organisms/NewOrExistingContactFlowModal';
import { RecipientDetailsContent } from '@/components/organisms/RecipientDetailsContent';
import { RecipientDetailsDrawer } from '@/components/organisms/RecipientDetailsDrawer';
import { RecipientPaymentMethods } from '@/components/organisms/RecipientPaymentMethods';
import { SendingFundsFlowModal } from '@/components/organisms/SendingFundsFlowModal';
import { SettingsAppearanceTab } from '@/components/organisms/SettingsAppearanceTab';
import { SettingsLanguageTab } from '@/components/organisms/SettingsLanguageTab';
import { SettingsLoginSecurityTab } from '@/components/organisms/SettingsLoginSecurityTab';
import { SettingsNotificationsTab } from '@/components/organisms/SettingsNotificationsTab';
import { SettingsTimeZoneTab } from '@/components/organisms/SettingsTimeZoneTab';
import { SettingsUsersTab } from '@/components/organisms/SettingsUsersTab';
import { TransactionDetailsDrawer } from '@/components/organisms/TransactionDetailsDrawer';
import {
  FF_DASHBOARD_CUSTOMER_MANAGEMENT,
  FF_DASHBOARD_FX_MARKUP,
  FF_DASHBOARD_INTEGRATOR_EARNINGS_WITHDRAWAL,
} from '@/data/featureFlags';
import { useCustomer } from '@/hooks/useCustomer';
import { usePermissionsManager } from '@/hooks/usePermissionsManager';
import { Activity } from '@/Screens/Dashboard/Activity';
import { Contacts } from '@/Screens/Dashboard/Contacts';
import { PaymentMethodModal } from '@/Screens/Dashboard/Contacts/PaymentMethodModal';
import { RecipientDetailsModal } from '@/Screens/Dashboard/Contacts/RecipientDetailsModal';
import { Customers } from '@/Screens/Dashboard/Customers';
import { AddCustomerModal } from '@/Screens/Dashboard/Customers/AddCustomerModal';
import { CustomerDepositFlow } from '@/Screens/Dashboard/Customers/CustomerDepositFlow';
import { CustomerDepositCompleted } from '@/Screens/Dashboard/Customers/CustomerDepositFlow/CustomerDepositCompleted';
import { CustomerDepositPayment } from '@/Screens/Dashboard/Customers/CustomerDepositFlow/CustomerDepositPayment';
import { CustomerDepositPending } from '@/Screens/Dashboard/Customers/CustomerDepositFlow/CustomerDepositPending';
import { CustomerDetails } from '@/Screens/Dashboard/Customers/CustomerDetails';
import { CustomerTransferFlow } from '@/Screens/Dashboard/Customers/CustomerTransferFlow';
import { CustomerTransferCompleted } from '@/Screens/Dashboard/Customers/CustomerTransferFlow/CustomerTransferCompleted';
import { CustomerTransferPayment } from '@/Screens/Dashboard/Customers/CustomerTransferFlow/CustomerTransferPayment';
import { ResumeVerificationModal } from '@/Screens/Dashboard/Customers/ResumeVerificationModal';
import { CryptoTxHashModal } from '@/Screens/Dashboard/Flows/common/CryptoTxHashModal';
import { ConvertFlow } from '@/Screens/Dashboard/Flows/ConvertFlow';
import { ConvertModal } from '@/Screens/Dashboard/Flows/ConvertFlow/ConvertModal';
import { DepositFlow } from '@/Screens/Dashboard/Flows/DepositFlow';
import { DepositCompleted } from '@/Screens/Dashboard/Flows/DepositFlow/DepositCompleted';
import { DepositPayment } from '@/Screens/Dashboard/Flows/DepositFlow/DepositPayment';
import { DepositPending } from '@/Screens/Dashboard/Flows/DepositFlow/DepositPending';
import { TransferFlow } from '@/Screens/Dashboard/Flows/TransferFlow';
import { TransferCompleted } from '@/Screens/Dashboard/Flows/TransferFlow/TransferCompleted';
import { TransferPayment } from '@/Screens/Dashboard/Flows/TransferFlow/TransferPayment';
import { WithdrawalFlow } from '@/Screens/Dashboard/Flows/WithdrawalFlow';
import { WithdrawalCompleted } from '@/Screens/Dashboard/Flows/WithdrawalFlow/WithdrawalCompleted';
import { Help } from '@/Screens/Dashboard/Help';
import { Home } from '@/Screens/Dashboard/Home';
import { IntegratorEarnings } from '@/Screens/Dashboard/IntegratorEarnings';
import { EarningsWithdrawalFlow } from '@/Screens/Dashboard/IntegratorEarnings/EarningsWithdrawalFlow';
import { EarningsWithdrawalCompleted } from '@/Screens/Dashboard/IntegratorEarnings/EarningsWithdrawalFlow/EarningsWithdrawalCompleted';
import { Profile } from '@/Screens/Dashboard/Profile';
import { Settings } from '@/Screens/Dashboard/Settings';
import { Wallet } from '@/Screens/Dashboard/Wallet';
import { Permission } from '@/types/permissions';

export const Dashboard: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const customer = useCustomer();

  const permissionsManager = usePermissionsManager();
  const initialSettingsTab = React.useMemo((): string => {
    return permissionsManager.userIsAuthorizedTo(Permission.MANAGE_USER)
      ? 'users'
      : 'login-and-security';
  }, [permissionsManager]);

  const onClose = React.useCallback((): void => {
    navigate('/home', { replace: true });
  }, [navigate]);

  if (customer.keycloakId === '') {
    return (
      <Routes>
        <Route path="/" element={<DashboardLayout />}>
          <Route
            path=""
            element={
              <>
                <Home />
                <FullScreenLoader loading={true} />
              </>
            }
          />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="*" element={<DashboardLayout />}>
        <Route path="home" element={<Home />}>
          <Route
            path="deposit"
            element={<Authorized permission={Permission.CREATE_TRANSACTIONS} />}>
            <Route path="*" element={<DepositFlow />}>
              <Route path="" element={null} index={true} />
              <Route path="*" element={<ModalFlow parentPath="/home/deposit" />}>
                <Route path="payment" element={<DepositPayment />} />
                <Route path="pending" element={<DepositPending />} />
                <Route path="hash" element={<CryptoTxHashModal />} />
                <Route path="completed" element={<DepositCompleted />} />

                <Route path="*" element={<Navigate to="/home/deposit" replace={true} />} />
              </Route>
            </Route>
          </Route>
          <Route
            path="transfer"
            element={<Authorized permission={Permission.CREATE_TRANSACTIONS} />}>
            <Route path="*" element={<TransferFlow />}>
              {/* This is here because otherwise the modal is rendered */}
              <Route path="" element={null} />
              <Route path="recipient" element={<ModalFlow parentPath="/home/transfer" />}>
                <Route path="" element={<NewOrExistingContactFlowModal />} />
                <Route path="new-contact">
                  <Route
                    path=""
                    element={
                      <SendingFundsFlowModal successPath="country/:country/account-type/:accountType/recipient-details" />
                    }
                  />
                  <Route path="country/:country/account-type/:accountType">
                    <Route path="recipient-details" element={<RecipientDetailsModal />} />
                    <Route
                      path="contact/:contactId/payment-method"
                      element={<PaymentMethodModal />}
                    />
                    <Route path="*" element={<Navigate to="/home/transfer/recipient" />} />
                  </Route>
                  <Route path="*" element={<Navigate to="/home/transfer/recipient" />} />
                </Route>
                <Route path="existing-contact">
                  <Route path="" element={<ExistingContactFlowModal />} />
                  <Route
                    path="contact/:contactId"
                    element={
                      <SendingFundsFlowModal successPath="country/:country/account-type/:accountType/recipient-details" />
                    }
                  />
                  <Route
                    path="contact/:contactId/country/:country/account-type/:accountType/recipient-details"
                    element={<PaymentMethodModal />}
                  />
                </Route>

                <Route path="*" element={<Navigate to="/home/transfer/recipient" />} />
              </Route>
              <Route path="*" element={<ModalFlow parentPath="/home/transfer" />}>
                <Route path="payment" element={<TransferPayment />} />
                <Route path="hash" element={<CryptoTxHashModal />} />
                <Route path="completed" element={<TransferCompleted />} />

                <Route path="*" element={<Navigate to="/home/transfer" replace={true} />} />
              </Route>
            </Route>
          </Route>
          <Route
            path="withdraw"
            element={<Authorized permission={Permission.CREATE_TRANSACTIONS} />}>
            <Route path="*" element={<WithdrawalFlow />}>
              <Route path="" element={null} />
              <Route path="account" element={<ModalFlow parentPath="/home/withdraw" />}>
                <Route
                  path=""
                  element={
                    <SendingFundsFlowModal successPath="country/:country/account-type/:accountType" />
                  }
                />
                <Route path="country/:country/account-type/:accountType">
                  <Route path="" element={<PaymentMethodModal />} />
                  <Route path="*" element={<Navigate to="/contacts" />} />
                </Route>
                <Route path="*" element={<Navigate to="/home/withdraw/account" replace={true} />} />
              </Route>
              <Route path="completed" element={<ModalFlow parentPath="/home/withdraw" />}>
                <Route path="" element={<WithdrawalCompleted />} />
              </Route>
              <Route path="*" element={<Navigate to="/home/withdrawal" />} />
            </Route>
          </Route>
          <Route
            path="convert"
            element={<Authorized permission={Permission.CREATE_TRANSACTIONS} />}>
            <Route path="*" element={<ConvertFlow />}>
              <Route path="" element={<ConvertModal onClose={onClose} />} index={true} />
              <Route path="*" element={<Navigate to="/home/convert" />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/home" replace={true} />} />
        </Route>

        <Route path="activity" element={<Authorized permission={Permission.VIEW_ACTIVITY} />}>
          <Route path="" element={<Activity />}>
            <Route path=":transactionId" element={<TransactionDetailsDrawer />} />
          </Route>
        </Route>
        <Route path="wallet" element={<Wallet />} />

        <Route
          path="contacts"
          element={<Authorized permission={Permission.MANAGE_PAYMENT_CONTACTS} />}>
          <Route path="" element={<Contacts />}>
            <Route path="new/*" element={<ModalFlow parentPath="/contacts" />}>
              <Route path="" element={<AddContactFlowModal />} />

              <Route path="internal/country/:country/account-type/:accountType">
                <Route path="" element={<PaymentMethodModal />} />

                <Route path="*" element={<Navigate to="/contacts/new" />} />
              </Route>

              <Route path="external/country/:country/account-type/:accountType">
                <Route path="recipient-details" element={<RecipientDetailsModal />} />
                <Route path="contact/:contactId/payment-method" element={<PaymentMethodModal />} />

                <Route path="*" element={<Navigate to="/contacts/new" />} />
              </Route>

              <Route path="*" element={<Navigate to="/contacts/new" replace={true} />} />
            </Route>

            <Route path="account/:accountId" element={<DrawerContainer />}>
              <Route element={<RecipientDetailsDrawer />}>
                <Route path="details" element={<AccountDetailsContent />} />
                <Route path="payment-methods" element={<AccountPaymentMethods />} />
                <Route path="*" element={<AccountDetailsContent />} />
              </Route>
            </Route>

            <Route path="recipient/:contactId" element={<DrawerContainer />}>
              <Route element={<RecipientDetailsDrawer />}>
                <Route path="details" element={<RecipientDetailsContent />} />
                <Route path="payment-methods" element={<RecipientPaymentMethods />}>
                  <Route path="new/*" element={<ModalFlow parentPath=".." />}>
                    <Route
                      path=""
                      element={
                        <SendingFundsFlowModal successPath="country/:country/account-type/:accountType" />
                      }
                    />
                    <Route path="country/:country/account-type/:accountType">
                      <Route path="" element={<PaymentMethodModal />} />
                      <Route path="*" element={<Navigate to="/contacts" />} />
                    </Route>
                  </Route>
                </Route>
                <Route path="*" element={<RecipientDetailsContent />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/contacts" replace={true} />} />
          </Route>
        </Route>

        <Route path="customers" element={<Authorized permission={Permission.MANAGE_CUSTOMER} />}>
          <Route
            path=""
            element={
              <IntegratorFeatureFlagged flagKey={FF_DASHBOARD_CUSTOMER_MANAGEMENT}>
                <Customers />
              </IntegratorFeatureFlagged>
            }>
            <Route path="new" element={<AddCustomerModal />} />
          </Route>
          <Route
            path=":customerId"
            element={
              <IntegratorFeatureFlagged flagKey={FF_DASHBOARD_CUSTOMER_MANAGEMENT}>
                <CustomerDetails />
              </IntegratorFeatureFlagged>
            }>
            <Route path="resume-verification" element={<ResumeVerificationModal />} />
            <Route path="activity/:transactionId" element={<TransactionDetailsDrawer />} />
            <Route
              path="transfer"
              element={<Authorized permission={Permission.CREATE_TRANSACTIONS} />}>
              <Route path="*" element={<CustomerTransferFlow />}>
                <Route path="" element={null} />
                <Route
                  path="recipient"
                  element={<ModalFlow parentPath="/customers/:customerId/transfer" />}>
                  <Route path="" element={<NewOrExistingContactFlowModal />} />
                  <Route path="new-contact">
                    <Route
                      path=""
                      element={
                        <SendingFundsFlowModal successPath="country/:country/account-type/:accountType/recipient-details" />
                      }
                    />
                    <Route path="country/:country/account-type/:accountType">
                      <Route path="recipient-details" element={<RecipientDetailsModal />} />
                      <Route
                        path="contact/:contactId/payment-method"
                        element={<PaymentMethodModal />}
                      />
                      <Route path="*" element={<Navigate to="../" />} />
                    </Route>
                    <Route path="*" element={<Navigate to="../" />} />
                  </Route>
                  <Route path="existing-contact">
                    <Route path="" element={<ExistingContactFlowModal />} />
                    <Route
                      path="contact/:contactId"
                      element={
                        <SendingFundsFlowModal successPath="country/:country/account-type/:accountType/recipient-details" />
                      }
                    />
                    <Route
                      path="contact/:contactId/country/:country/account-type/:accountType/recipient-details"
                      element={<PaymentMethodModal />}
                    />
                  </Route>
                  <Route path="*" element={<Navigate to="../" />} />
                </Route>
                <Route
                  path="*"
                  element={<ModalFlow parentPath="/customers/:customerId/transfer" />}>
                  <Route path="payment" element={<CustomerTransferPayment />} />
                  <Route path="hash" element={<CryptoTxHashModal />} />
                  <Route path="completed" element={<CustomerTransferCompleted />} />
                  <Route path="*" element={<Navigate to="../" replace={true} />} />
                </Route>
              </Route>
            </Route>
            <Route
              path="deposit"
              element={<Authorized permission={Permission.CREATE_TRANSACTIONS} />}>
              <Route path="*" element={<CustomerDepositFlow />}>
                <Route path="" element={null} index={true} />
                <Route path="*" element={<CustomerDepositModalFlow />}>
                  <Route path="payment" element={<CustomerDepositPayment />} />
                  <Route path="pending" element={<CustomerDepositPending />} />
                  <Route path="hash" element={<CryptoTxHashModal />} />
                  <Route path="completed" element={<CustomerDepositCompleted />} />

                  <Route path="*" element={<Navigate to="" replace={true} />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>

        <Route
          path="earnings"
          element={
            <IntegratorFeatureFlagged flagKey={FF_DASHBOARD_FX_MARKUP}>
              <IntegratorEarnings />
            </IntegratorFeatureFlagged>
          }>
          <Route path=":transactionId" element={<TransactionDetailsDrawer />} />
        </Route>
        <Route
          path="earnings/withdraw"
          element={
            <IntegratorFeatureFlagged flagKey={FF_DASHBOARD_INTEGRATOR_EARNINGS_WITHDRAWAL}>
              <Authorized permission={Permission.WITHDRAW_INTEGRATOR_EARNINGS} />
            </IntegratorFeatureFlagged>
          }>
          <Route path="" element={<EarningsWithdrawalFlow />} />
          <Route path="completed" element={<ModalFlow parentPath="/earnings/withdraw" />}>
            <Route path="" element={<EarningsWithdrawalCompleted />} />
          </Route>
        </Route>

        <Route path="profile" element={<Profile />} />
        <Route path="help" element={<Help />} />
        <Route path="settings" element={<Settings />}>
          <Route path="users" element={<Authorized permission={Permission.MANAGE_USER} />}>
            <Route path="" element={<SettingsUsersTab />}>
              <Route
                path="roles"
                element={
                  <Dialog open={true}>
                    <Outlet />
                  </Dialog>
                }>
                <Route path="" element={<ManageRolesModal />} />
                <Route path="edit">
                  <Route path=":groupId" element={<ManageRolesModal />} />
                </Route>
                <Route path="new" element={<CreateGroupModal />} />
              </Route>
              <Route path="new" element={<InviteUserModal />} />
              <Route path=":userId" element={<EditUserModal />} />
            </Route>
          </Route>
          <Route path="login-and-security" element={<SettingsLoginSecurityTab />} />
          <Route path="notifications" element={<SettingsNotificationsTab />} />
          <Route path="language" element={<SettingsLanguageTab />} />
          <Route path="time-zone" element={<SettingsTimeZoneTab />} />
          <Route path="appearance" element={<SettingsAppearanceTab />} />
          <Route path="*" element={<Navigate to={initialSettingsTab} replace={true} />} />
        </Route>

        <Route path="*" element={<Navigate to="/home" replace={true} />} />
      </Route>
    </Routes>
  );
};

const CustomerDepositModalFlow: React.FC = (): React.ReactElement => {
  const { customerId = '' } = useParams<{ readonly customerId: string }>();
  return <ModalFlow parentPath={`/customers/${customerId}/deposit`} />;
};
